import React from 'react';
import { CheckCircle, X } from 'lucide-react';
import { motion } from 'framer-motion';

const comparisons = [
  {
    feature: 'All-in-One Platform',
    zylu: true,
    others: false,
    description: 'Complete salon management in one integrated system'
  },
  {
    feature: 'Automated Marketing',
    zylu: true,
    others: 'Limited',
    description: 'Smart campaigns that run themselves'
  },
  {
    feature: 'Real-time Analytics',
    zylu: true,
    others: 'Basic',
    description: 'Actionable insights for better decisions'
  },
  {
    feature: 'Custom Loyalty Programs',
    zylu: true,
    others: false,
    description: 'Personalized rewards that increase retention'
  },
  {
    feature: 'Mobile-First Design',
    zylu: true,
    others: 'Limited',
    description: 'Optimized for on-the-go management'
  },
  {
    feature: 'Training unlimited',
    zylu: true,
    others: 'Extra Cost',
    description: 'Complete onboarding at no additional charge'
  },
  {
    feature: 'Money-Back Guarantee',
    zylu: true,
    others: false,
    description: '14-day risk-free trial period'
  }
];

const WhyZyluOnly: React.FC = () => {
  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-black mb-6">
            Why Zylu ?
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            See how Zylu compares to other salon management solutions and why thousands of salon owners choose us
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          viewport={{ once: true }}
          className="bg-gray-50 rounded-2xl p-8 overflow-hidden"
        >
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-gray-200">
                  <th className="text-left py-4 px-6 font-bold text-gray-900">Feature</th>
                  <th className="text-center py-4 px-6 font-bold text-red-600 bg-red-50 rounded-t-lg">
                    Zylu
                  </th>
                  <th className="text-center py-4 px-6 font-bold text-gray-600">
                    Other Solutions
                  </th>
                </tr>
              </thead>
              <tbody>
                {comparisons.map((item, index) => (
                  <motion.tr
                    key={index}
                    initial={{ opacity: 0, x: -50 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.05 }}
                    viewport={{ once: true }}
                    className="border-b border-gray-200 hover:bg-gray-100 transition-colors"
                  >
                    <td className="py-4 px-6">
                      <div>
                        <div className="font-semibold text-gray-900">{item.feature}</div>
                        <div className="text-sm text-gray-600 mt-1">{item.description}</div>
                      </div>
                    </td>
                    <td className="py-4 px-6 text-center bg-red-50">
                      {item.zylu === true ? (
                        <CheckCircle className="h-6 w-6 text-green-500 mx-auto" />
                      ) : (
                        <span className="text-red-600 font-semibold">{item.zylu}</span>
                      )}
                    </td>
                    <td className="py-4 px-6 text-center">
                      {item.others === true ? (
                        <CheckCircle className="h-6 w-6 text-green-500 mx-auto" />
                      ) : item.others === false ? (
                        <X className="h-6 w-6 text-red-500 mx-auto" />
                      ) : (
                        <span className="text-gray-600 font-semibold">{item.others}</span>
                      )}
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          viewport={{ once: true }}
          className="text-center mt-12"
        >
          <div className="bg-red-600 text-white rounded-2xl p-8 max-w-4xl mx-auto">
            <h3 className="text-3xl font-bold mb-4">
              The Bottom Line
            </h3>
            <p className="text-xl mb-6">
              While other solutions force you to juggle multiple tools and subscriptions, 
              Zylu gives you everything you need in one powerful, integrated platform.
            </p>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-white bg-opacity-20 rounded-lg p-4">
                <div className="text-2xl font-bold mb-2">1 Platform</div>
                <p className="text-sm">Instead of 5-10 different tools</p>
              </div>
              <div className="bg-white bg-opacity-20 rounded-lg p-4">
                <div className="text-2xl font-bold mb-2">1 Login</div>
                <p className="text-sm">Access everything from anywhere</p>
              </div>
              <div className="bg-white bg-opacity-20 rounded-lg p-4">
                <div className="text-2xl font-bold mb-2">1 Support Team</div>
                <p className="text-sm">No more vendor finger-pointing</p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default WhyZyluOnly;